import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Typography } from "@material-ui/core";
import { useSearchParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import UserModal from "./UserModal";
import User from "../../model/user";
import Role from "../../model/role";
import Misc from "../../utils/Misc";
import useStyles from "./styles";
import { PrimaryColor } from "../../shared/styles/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "35rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  user: User;
  roles: Role[];
  resetList?: () => void;
  isExternal?: boolean;
  onOpen?: (id?: string) => void;
  onClose?: () => void;
}

const UserDetailModal = ({
  user,
  roles,
  resetList,
  isExternal,
  onOpen,
  onClose,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOpen = () => {
    setOpen(true);
    if (onOpen) {
      onOpen(user.id?.toString());
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const userId = searchParams.get("userId");
    if (!open && userId && userId === user.id?.toString()) {
      handleOpen();
    }
  }, [searchParams]);

  return (
    <div>
      <Button onClick={handleOpen}>
        {!isExternal ? (
          <MoreVertIcon sx={{ color: PrimaryColor }} />
        ) : (
          <Typography style={{ color: PrimaryColor, textTransform: "none" }}>
            {user.firstName} {user.lastName}
          </Typography>
        )}
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        <Box sx={style}>
          <div className={classes.headerDiv}>
            <Typography className={classes.title}>User info</Typography>
            <div className={classes.headerSubDiv}>
              <UserModal user={user} roles={roles} resetList={resetList} />
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
          </div>
          <hr />
          <div className={classes.form}>
            <div className={classes.inputDiv}>
              <Typography className={classes.label}>Name</Typography>
              <Typography className={classes.inputDetail}>
                {user.firstName} {user.lastName}
              </Typography>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Email</Typography>
                <Typography className={classes.inputDetail}>
                  {user.email}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Phone</Typography>
                <Typography className={classes.inputDetail}>
                  {Misc.formatPhoneNumber(user.phone)}
                </Typography>
              </div>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>
                  Street address
                </Typography>
                <Typography className={classes.inputDetail}>
                  {user.street}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>Zip code</Typography>
                <Typography className={classes.inputDetail}>
                  {user.zip}
                </Typography>
              </div>
            </div>
            <div className={classes.inputGroup}>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>City</Typography>
                <Typography className={classes.inputDetail}>
                  {user.city}
                </Typography>
              </div>
              <div className={classes.inputDiv}>
                <Typography className={classes.label}>State</Typography>
                <Typography className={classes.inputDetail}>
                  {user.state}
                </Typography>
              </div>
            </div>
            <div className={classes.inputDiv}>
              <Typography className={classes.label}>Role</Typography>
              <Typography className={classes.inputDetail}>
                {user.Role?.name}
              </Typography>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default UserDetailModal;
